/**
 * Поля клипа в карточке кадра видео-проекта (спека §9.2, v2 §1.2): «камера», «видео-промпт» — правка на
 * месте, «переход» — список, «лицо в кадре / без лица» (`shot_face`: модель, отклоняющая лица, отдаёт
 * кадр с лицом запасной), и ряд плашек клипов по моделям (модель · статус · цена; готовый — в карточку
 * результата). Сохранение — та же ручка `shot_field`, история правок — на странице кадра.
 * Длительность правится на хронологии и на странице кадра.
 */

import { useQueryClient } from "@tanstack/react-query";

import { InlineText } from "@/components/project/InlineText";
import { ErrorLine } from "@/components/project/ui";
import { errorText, qk, type ProjectView } from "@/lib/project";
import {
  SHOT_TRANSITION_LABEL,
  useShotField,
  type ShotTransition,
  type ShotVideoField,
  type TimelineShot,
} from "@/lib/video";

import { FaceToggle } from "./bits";
import { ClipPill, useModelLabels } from "./clips";

const FIELD_KEY: Record<ShotVideoField, keyof TimelineShot> = {
  shot_duration_ms: "duration_ms",
  shot_camera: "camera",
  shot_video_prompt: "video_prompt",
  shot_transition: "transition",
  shot_face: "face",
};

/** Пустое значение поля в хронологии: у лица пусто = «в кадре» (как отдаёт сервер). */
const EMPTY: Partial<Record<ShotVideoField, string>> = { shot_face: "visible" };

export function ShotVideoFields({ pid, shot }: { pid: number; shot: TimelineShot }) {
  const client = useQueryClient();
  const field = useShotField(pid);
  const label = useModelLabels();

  /** Сохранить и сразу показать ответ сервера в карточке проекта — до перечитывания. */
  const save = async (name: ShotVideoField, value: string): Promise<string> => {
    const result = await field.mutateAsync({ sid: shot.shot_id, field: name, value });
    client.setQueryData<ProjectView>(qk.project(pid), (old) =>
      old?.video
        ? {
            ...old,
            video: {
              ...old.video,
              timeline: old.video.timeline.map((t) =>
                t.shot_id === shot.shot_id ? { ...t, [FIELD_KEY[name]]: result.value || (EMPTY[name] ?? null) } : t,
              ),
            },
          }
        : old,
    );
    void client.invalidateQueries({ queryKey: qk.shot(pid, shot.shot_id) });
    return result.value;
  };

  const transitionBusy = field.isPending && field.variables?.field === "shot_transition";
  const faceBusy = field.isPending && field.variables?.field === "shot_face";

  return (
    <div className="grid gap-1 rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5" data-annot="поля клипа">
      <div className="grid grid-cols-[88px_minmax(0,1fr)] items-baseline gap-x-2 gap-y-1 text-[12.5px]">
        <span className="text-[11.5px] text-fg-faint">камера</span>
        <InlineText
          value={shot.camera}
          placeholder="не задана — например, slow dolly-in"
          onSave={(v) => save("shot_camera", v)}
          className="min-w-0"
          annot="камера кадра"
        />
        <span className="text-[11.5px] text-fg-faint">видео-промпт</span>
        <InlineText
          value={shot.video_prompt}
          multiline
          placeholder="не задан — без него клип не запустится"
          onSave={(v) => save("shot_video_prompt", v)}
          className="min-w-0 text-fg-muted"
          annot="видео-промпт кадра"
        />
        <span className="text-[11.5px] text-fg-faint">переход</span>
        <div className="flex items-center gap-2">
          <select
            value={shot.transition ?? ""}
            disabled={transitionBusy}
            onChange={(e) => void save("shot_transition", e.target.value).catch(() => undefined)}
            aria-label="переход после кадра"
            className="h-7 rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 text-[12px] text-fg outline-none focus:border-accent disabled:opacity-50"
          >
            <option value="">как у монтажа</option>
            {(Object.keys(SHOT_TRANSITION_LABEL) as ShotTransition[]).map((t) => (
              <option key={t} value={t}>
                {SHOT_TRANSITION_LABEL[t]}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-fg-faint">после кадра</span>
        </div>
        <span className="text-[11.5px] text-fg-faint">лицо</span>
        <div>
          <FaceToggle
            value={shot.face}
            disabled={faceBusy}
            onChange={(next) => void save("shot_face", next).catch(() => undefined)}
          />
        </div>
      </div>
      <ErrorLine
        text={
          field.isError && (field.variables?.field === "shot_transition" || field.variables?.field === "shot_face")
            ? errorText(field.error)
            : ""
        }
      />
      {shot.clips.length ? (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {shot.clips.map((c) => (
            <ClipPill key={c.generation_id} clip={c} label={label(c.model)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
